module Geisha.Services {
    export interface IExternalDialResult {
        accepted: boolean;
        handedOffToExistingTab: boolean;
        phoneNumber: string;
    }

    interface IExternalDialRequest {
        id: string;
        phoneNumber: string;
        source: string;
        sourceTabId: string;
        targetTabId?: string;
        createdAt: number;
    }

    interface IPrimaryTabRecord {
        tabId: string;
        updatedAt: number;
    }

    export class RingCentralService {
        public static $inject = ["$window", "$document"];

        private readonly allowedRoles = ["administrator", "manager", "receptionist"];
        private readonly activeCallStatuses = ["setup", "proceeding", "ringing", "callconnected", "connected", "onhold", "hold", "parked"];
        private readonly inactiveCallStatuses = ["idle", "disconnected", "gone", "nocall", "ended", "finished"];
        private readonly beforeUnloadMessage = "A RingCentral call is in progress. Are you sure you want to leave Atom?";
        private readonly scriptId = "ringcentral-embeddable-adapter";
        private readonly widgetPrefix = "rc-widget";
        private readonly maxMinimizeAttempts = 60;
        private readonly pendingDialRetryMs = 750;
        private readonly presenceHeartbeatMs = 10000;
        private readonly presenceExpiryMs = 30000;
        private readonly storagePrefix = "atom:ringcentral:";
        private readonly tabIdStorageKey = this.storagePrefix + "tab-id";
        private readonly primaryTabKey = this.storagePrefix + "primary-tab";
        private readonly dialRequestKey = this.storagePrefix + "dial-request";
        private readonly presenceKeyPrefix = this.storagePrefix + "presence:";
        private listenersBound = false;
        private scriptInjected = false;
        private callSessionActive = false;
        private minimizeRequested = false;
        private iframeLoadBound = false;
        private iframeInteractionBound = false;
        private userOpenedWidget = false;
        private minimizeTimerId: number = null;
        private pendingDialTimerId: number = null;
        private presenceTimerId: number = null;
        private readonly tabId: string;
        private isCurrentUserEligible = false;
        private isPrimaryTab = false;
        private rcLoggedIn = false;
        private rcDialerReady = false;
        private pendingExternalDialNumber = "";
        private pendingExternalDialRequestId = "";
        private lastHandledExternalDialRequestId = "";

        constructor(private $window: ng.IWindowService, private $document: ng.IDocumentService) {
            this.tabId = this.getOrCreateTabId();
        }

        public syncForCurrentUser(isAuthenticated: boolean, role: string) {
            this.isCurrentUserEligible = !!isAuthenticated && this.isAllowedRole(role);

            if (!this.isCurrentUserEligible) {
                this.callSessionActive = false;
                this.userOpenedWidget = false;
                this.rcLoggedIn = false;
                this.rcDialerReady = false;
                this.cancelScheduledMinimize();
                this.cancelPendingDialProcessing();
                this.stopPresenceHeartbeat();
                this.updateBeforeUnloadGuard();
                this.setWidgetVisibility(false);
                return;
            }

            this.bindListenersOnce();
            this.startPresenceHeartbeat();
            this.injectWidgetScriptOnce();
            this.bindWidgetFrameLoadOnce();
            this.bindWidgetFrameInteractionOnce();
            this.setWidgetVisibility(true);
            this.scheduleMinimizeIfIdle();
            this.schedulePendingDialProcessing(300);
        }

        public clickToCall(phoneNumber: string) {
            var sanitizedNumber = this.sanitisePhoneNumber(phoneNumber);
            if (!sanitizedNumber) {
                return false;
            }

            this.clearPendingDialRequest();
            return this.tryPlaceCallNow(sanitizedNumber);
        }

        public beginExternalDial(phoneNumber: string, source?: string): IExternalDialResult {
            var sanitizedNumber = this.sanitisePhoneNumber(phoneNumber);
            if (!sanitizedNumber) {
                return {
                    accepted: false,
                    handedOffToExistingTab: false,
                    phoneNumber: ""
                };
            }

            var targetTabId = this.getActivePrimaryTabId();
            var request: IExternalDialRequest = {
                id: this.createRequestId(),
                phoneNumber: sanitizedNumber,
                source: source || "external",
                sourceTabId: this.tabId,
                createdAt: Date.now()
            };

            if (targetTabId && targetTabId !== this.tabId) {
                request.targetTabId = targetTabId;
                this.broadcastDialRequest(request);
                return {
                    accepted: true,
                    handedOffToExistingTab: true,
                    phoneNumber: sanitizedNumber
                };
            }

            this.acceptExternalDialRequest(request);
            return {
                accepted: true,
                handedOffToExistingTab: false,
                phoneNumber: sanitizedNumber
            };
        }

        private tryPlaceCallNow(sanitizedNumber: string) {
            this.userOpenedWidget = true;
            this.cancelScheduledMinimize();
            this.setWidgetVisibility(true);

            var rcAdapter = (this.$window as any).RCAdapter;
            if (rcAdapter && typeof rcAdapter.setMinimized === "function") {
                rcAdapter.setMinimized(false);
            }

            if (rcAdapter && typeof rcAdapter.clickToCall === "function") {
                rcAdapter.clickToCall(sanitizedNumber, true);
                return true;
            }

            var widgetFrame = this.getWidgetFrame() as HTMLIFrameElement;
            if (widgetFrame && widgetFrame.contentWindow) {
                widgetFrame.contentWindow.postMessage({
                    type: "rc-adapter-new-call",
                    phoneNumber: sanitizedNumber,
                    toCall: true
                }, "*");
                return true;
            }

            return false;
        }

        private isAllowedRole(role: string) {
            if (!role) {
                return false;
            }

            return this.allowedRoles.indexOf(role.toLowerCase()) > -1;
        }

        private bindListenersOnce() {
            if (this.listenersBound) {
                return;
            }

            this.listenersBound = true;
            this.$window.addEventListener("message", this.handleWidgetMessage);
            this.$window.addEventListener("storage", this.handleStorageEvent);
            this.$window.addEventListener("unload", this.handleWindowUnload);
        }

        private injectWidgetScriptOnce() {
            if (this.scriptInjected || !GlobalAppSettings.RingCentralClientId) {
                return;
            }

            var existingScript = this.$document[0].getElementById(this.scriptId) as HTMLScriptElement;
            if (existingScript) {
                this.scriptInjected = true;
                return;
            }

            var widgetScript = this.$document[0].createElement("script");
            widgetScript.id = this.scriptId;
            widgetScript.async = true;
            widgetScript.src = this.buildWidgetUrl();
            widgetScript.onload = () => this.schedulePendingDialProcessing(300);
            widgetScript.onerror = function () {
                console.warn("RingCentral Embeddable 3.x failed to load.");
            };

            this.$document[0].body.appendChild(widgetScript);
            this.scriptInjected = true;
        }

        private buildWidgetUrl() {
            var queryString = [
                "clientId=" + encodeURIComponent(GlobalAppSettings.RingCentralClientId),
                "appServer=" + encodeURIComponent(GlobalAppSettings.RingCentralAppServer),
                "enableSideWidget=1",
                "enablePopup=1",
                "multipleTabsSupport=1",
                "disableInactiveTabCallEvent=1",
                "defaultCallWith=browser",
                "prefix=" + encodeURIComponent(this.widgetPrefix)
            ].join("&");

            return "https://apps.ringcentral.com/integration/ringcentral-embeddable/3.x/adapter.js?" + queryString;
        }

        private handleWidgetMessage = (event: MessageEvent) => {
            if (!this.isRingCentralMessage(event)) {
                return;
            }

            var data = event.data || {};
            switch (data.type) {
                case "rc-call-ring-notify":
                case "rc-call-init-notify":
                case "rc-call-start-notify":
                case "rc-call-hold-notify":
                case "rc-call-resume-notify":
                    if (this.isVisibleDocument()) {
                        this.setCallSessionActive(true);
                    }
                    break;
                case "rc-call-end-notify":
                    this.setCallSessionActive(false);
                    break;
                case "rc-active-call-notify":
                    this.syncCallStateFromEvent(data);
                    break;
                case "rc-login-status-notify":
                    this.rcLoggedIn = !!data.loggedIn;
                    if (this.rcLoggedIn) {
                        this.scheduleMinimizeIfIdle();
                        this.schedulePendingDialProcessing(200);
                    }
                    break;
                case "rc-dialer-status-notify":
                    this.rcDialerReady = !!data.ready;
                    if (this.rcDialerReady) {
                        this.scheduleMinimizeIfIdle();
                        this.schedulePendingDialProcessing(200);
                    }
                    break;
            }
        }

        private handleStorageEvent = (event: StorageEvent) => {
            if (!event.key) {
                return;
            }

            if (event.key === this.dialRequestKey && event.newValue) {
                var request = this.parseDialRequest(event.newValue);
                if (!request || request.sourceTabId === this.tabId) {
                    return;
                }

                if (request.targetTabId && request.targetTabId !== this.tabId) {
                    return;
                }

                this.acceptExternalDialRequest(request);
                return;
            }

            if (event.key.indexOf(this.presenceKeyPrefix) === 0 || event.key === this.primaryTabKey) {
                this.tryBecomePrimaryTab();
            }
        }

        private handleWindowUnload = () => {
            this.stopPresenceHeartbeat();
        }

        private acceptExternalDialRequest(request: IExternalDialRequest) {
            if (!request || !request.id || request.id === this.lastHandledExternalDialRequestId) {
                return;
            }

            var sanitizedNumber = this.sanitisePhoneNumber(request.phoneNumber);
            if (!sanitizedNumber) {
                return;
            }

            this.lastHandledExternalDialRequestId = request.id;
            this.pendingExternalDialRequestId = request.id;
            this.pendingExternalDialNumber = sanitizedNumber;
            this.tryFocusWindow();

            if (this.isCurrentUserEligible) {
                this.injectWidgetScriptOnce();
                this.bindWidgetFrameLoadOnce();
                this.bindWidgetFrameInteractionOnce();
                this.setWidgetVisibility(true);
            }

            this.schedulePendingDialProcessing(100);
        }

        private processPendingDialRequest() {
            if (!this.pendingExternalDialNumber || !this.isCurrentUserEligible) {
                return false;
            }

            this.injectWidgetScriptOnce();
            this.bindWidgetFrameLoadOnce();
            this.bindWidgetFrameInteractionOnce();
            this.setWidgetVisibility(true);

            var rcAdapter = (this.$window as any).RCAdapter;
            if (rcAdapter && typeof rcAdapter.clickToCall === "function") {
                if (this.tryPlaceCallNow(this.pendingExternalDialNumber)) {
                    this.clearPendingDialRequest();
                    return true;
                }
            }

            if (!this.rcLoggedIn && !this.rcDialerReady) {
                this.schedulePendingDialProcessing();
                return false;
            }

            if (this.tryPlaceCallNow(this.pendingExternalDialNumber)) {
                this.clearPendingDialRequest();
                return true;
            }

            this.schedulePendingDialProcessing();
            return false;
        }

        private clearPendingDialRequest() {
            this.pendingExternalDialNumber = "";
            this.pendingExternalDialRequestId = "";
            this.cancelPendingDialProcessing();
        }

        private schedulePendingDialProcessing(delayMs?: number) {
            if (!this.pendingExternalDialNumber) {
                return;
            }

            this.cancelPendingDialProcessing();
            this.pendingDialTimerId = this.$window.setTimeout(() => {
                this.pendingDialTimerId = null;
                this.processPendingDialRequest();
            }, typeof delayMs === "number" ? delayMs : this.pendingDialRetryMs);
        }

        private cancelPendingDialProcessing() {
            if (this.pendingDialTimerId !== null) {
                this.$window.clearTimeout(this.pendingDialTimerId);
                this.pendingDialTimerId = null;
            }
        }

        private tryFocusWindow() {
            try {
                this.$window.focus();
            } catch (e) {
            }
        }

        private getOrCreateTabId() {
            try {
                var existingTabId = this.$window.sessionStorage.getItem(this.tabIdStorageKey);
                if (existingTabId) {
                    return existingTabId;
                }

                var tabId = "tab-" + Math.random().toString(36).substr(2, 9) + "-" + Date.now();
                this.$window.sessionStorage.setItem(this.tabIdStorageKey, tabId);
                return tabId;
            } catch (e) {
                return "tab-" + Math.random().toString(36).substr(2, 9) + "-" + Date.now();
            }
        }

        private startPresenceHeartbeat() {
            this.writePresenceHeartbeat();

            if (this.presenceTimerId !== null) {
                return;
            }

            this.presenceTimerId = this.$window.setInterval(() => {
                this.writePresenceHeartbeat();
            }, this.presenceHeartbeatMs);
        }

        private stopPresenceHeartbeat() {
            if (this.presenceTimerId !== null) {
                this.$window.clearInterval(this.presenceTimerId);
                this.presenceTimerId = null;
            }

            try {
                this.$window.localStorage.removeItem(this.getPresenceKey(this.tabId));
                var activePrimaryTabId = this.getActivePrimaryTabId();
                if (activePrimaryTabId === this.tabId) {
                    this.$window.localStorage.removeItem(this.primaryTabKey);
                }
            } catch (e) {
            }

            this.isPrimaryTab = false;
        }

        private writePresenceHeartbeat() {
            try {
                this.cleanupStalePresenceRecords();
                this.$window.localStorage.setItem(this.getPresenceKey(this.tabId), Date.now().toString());
                this.tryBecomePrimaryTab();
            } catch (e) {
            }
        }

        private tryBecomePrimaryTab() {
            if (!this.isCurrentUserEligible) {
                this.isPrimaryTab = false;
                return;
            }

            var activePrimaryTabId = this.getActivePrimaryTabId();
            if (!activePrimaryTabId || activePrimaryTabId === this.tabId) {
                this.writePrimaryTabRecord();
                this.isPrimaryTab = true;
                return;
            }

            this.isPrimaryTab = false;
        }

        private writePrimaryTabRecord() {
            var record: IPrimaryTabRecord = {
                tabId: this.tabId,
                updatedAt: Date.now()
            };

            try {
                this.$window.localStorage.setItem(this.primaryTabKey, JSON.stringify(record));
            } catch (e) {
            }
        }

        private getActivePrimaryTabId() {
            var record = this.getPrimaryTabRecord();
            if (!record || !record.tabId || !this.isPresenceFresh(record.tabId, record.updatedAt)) {
                return "";
            }

            return record.tabId;
        }

        private getPrimaryTabRecord() {
            try {
                var rawRecord = this.$window.localStorage.getItem(this.primaryTabKey);
                if (!rawRecord) {
                    return null;
                }

                return JSON.parse(rawRecord) as IPrimaryTabRecord;
            } catch (e) {
                return null;
            }
        }

        private cleanupStalePresenceRecords() {
            try {
                var staleKeys: string[] = [];
                for (var i = 0; i < this.$window.localStorage.length; i++) {
                    var key = this.$window.localStorage.key(i);
                    if (!key || key.indexOf(this.presenceKeyPrefix) !== 0) {
                        continue;
                    }

                    var updatedAt = parseInt(this.$window.localStorage.getItem(key) || "", 10);
                    if (isNaN(updatedAt) || Date.now() - updatedAt > this.presenceExpiryMs) {
                        staleKeys.push(key);
                    }
                }

                staleKeys.forEach((key) => this.$window.localStorage.removeItem(key));

                var activePrimaryTabId = this.getActivePrimaryTabId();
                if (!activePrimaryTabId) {
                    this.$window.localStorage.removeItem(this.primaryTabKey);
                }
            } catch (e) {
            }
        }

        private isPresenceFresh(tabId: string, updatedAt?: number) {
            if (!tabId) {
                return false;
            }

            var lastSeenAt = updatedAt;
            if (typeof lastSeenAt !== "number") {
                try {
                    lastSeenAt = parseInt(this.$window.localStorage.getItem(this.getPresenceKey(tabId)) || "", 10);
                } catch (e) {
                    lastSeenAt = NaN;
                }
            }

            return !isNaN(lastSeenAt) && Date.now() - lastSeenAt <= this.presenceExpiryMs;
        }

        private getPresenceKey(tabId: string) {
            return this.presenceKeyPrefix + tabId;
        }

        private createRequestId() {
            return "dial-" + Date.now() + "-" + Math.random().toString(36).substr(2, 9);
        }

        private broadcastDialRequest(request: IExternalDialRequest) {
            try {
                this.$window.localStorage.setItem(this.dialRequestKey, JSON.stringify(request));
            } catch (e) {
            }
        }

        private parseDialRequest(rawRequest: string) {
            try {
                return JSON.parse(rawRequest) as IExternalDialRequest;
            } catch (e) {
                return null;
            }
        }

        private isRingCentralMessage(event: MessageEvent) {
            var data = event.data || {};
            if (!data.type || data.type.indexOf("rc-") !== 0) {
                return false;
            }

            return typeof event.origin === "string" && event.origin.indexOf("ringcentral.com") > -1;
        }

        private syncCallStateFromEvent(data: any) {
            var call = data.call;
            if (!call && data.calls && data.calls.length) {
                call = data.calls[0];
            }

            if (!call) {
                this.setCallSessionActive(false);
                return;
            }

            var normalizedStatus = this.normaliseStatus(call.telephonyStatus || call.status);
            if (!normalizedStatus) {
                this.setCallSessionActive(true);
                return;
            }

            if (this.activeCallStatuses.indexOf(normalizedStatus) > -1) {
                this.setCallSessionActive(true);
                return;
            }

            if (this.inactiveCallStatuses.indexOf(normalizedStatus) > -1) {
                this.setCallSessionActive(false);
            }
        }

        private normaliseStatus(status: string) {
            if (!status) {
                return "";
            }

            return status.toLowerCase().replace(/[^a-z]/g, "");
        }

        private setCallSessionActive(isActive: boolean) {
            this.callSessionActive = isActive;
            if (isActive) {
                this.cancelScheduledMinimize();
            }
            this.updateBeforeUnloadGuard();
            if (!isActive) {
                this.scheduleMinimizeIfIdle();
            }
        }

        private updateBeforeUnloadGuard() {
            this.$window.onbeforeunload = this.callSessionActive ? this.beforeUnloadHandler : null;
        }

        private beforeUnloadHandler = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = this.beforeUnloadMessage;
            return this.beforeUnloadMessage;
        }

        private setWidgetVisibility(isVisible: boolean) {
            var widgetFrame = this.$document[0].getElementById(this.widgetPrefix + "-adapter-frame");
            if (!widgetFrame) {
                return;
            }

            widgetFrame.style.display = isVisible ? "block" : "none";
        }

        private isVisibleDocument() {
            return !this.$document[0].hidden;
        }

        private bindWidgetFrameLoadOnce() {
            if (this.iframeLoadBound) {
                return;
            }

            var widgetFrame = this.getWidgetFrame();
            if (!widgetFrame) {
                this.$window.setTimeout(() => this.bindWidgetFrameLoadOnce(), 500);
                return;
            }

            this.iframeLoadBound = true;
            widgetFrame.addEventListener("load", () => {
                this.scheduleMinimizeIfIdle();
                this.schedulePendingDialProcessing(200);
            });
        }

        private bindWidgetFrameInteractionOnce() {
            if (this.iframeInteractionBound) {
                return;
            }

            var widgetFrame = this.getWidgetFrame();
            if (!widgetFrame) {
                this.$window.setTimeout(() => this.bindWidgetFrameInteractionOnce(), 500);
                return;
            }

            this.iframeInteractionBound = true;
            var cancelAutoMinimize = () => {
                this.userOpenedWidget = true;
                this.cancelScheduledMinimize();
            };

            widgetFrame.addEventListener("mousedown", cancelAutoMinimize);
            widgetFrame.addEventListener("pointerdown", cancelAutoMinimize);
            widgetFrame.addEventListener("touchstart", cancelAutoMinimize);
            widgetFrame.addEventListener("focus", cancelAutoMinimize);
        }

        private scheduleMinimizeIfIdle() {
            if (this.callSessionActive || this.minimizeRequested || this.userOpenedWidget) {
                return;
            }

            this.minimizeRequested = true;
            this.tryMinimizeWidget(0);
        }

        private tryMinimizeWidget(attempt: number) {
            if (this.userOpenedWidget) {
                this.cancelScheduledMinimize();
                return;
            }

            var rcAdapter = (this.$window as any).RCAdapter;
            if (!this.callSessionActive && rcAdapter && typeof rcAdapter.setMinimized === "function") {
                rcAdapter.setMinimized(true);
                this.cancelScheduledMinimize();
                return;
            }

            if (attempt >= this.maxMinimizeAttempts || this.callSessionActive) {
                this.cancelScheduledMinimize();
                return;
            }

            this.minimizeTimerId = this.$window.setTimeout(() => this.tryMinimizeWidget(attempt + 1), 500);
        }

        private getWidgetFrame(): HTMLElement {
            return this.$document[0].getElementById(this.widgetPrefix + "-adapter-frame");
        }

        private cancelScheduledMinimize() {
            this.minimizeRequested = false;
            if (this.minimizeTimerId !== null) {
                this.$window.clearTimeout(this.minimizeTimerId);
                this.minimizeTimerId = null;
            }
        }

        private sanitisePhoneNumber(phoneNumber: string) {
            if (!phoneNumber) {
                return "";
            }

            return phoneNumber.replace(/[^\d+]/g, "");
        }
    }
}
